// App.js
// Root component — providers + routing
//
// Provider hierarchy (outside → inside):
// 1. ThemeContextProvider (needs to wrap everything for RTL + theme)
// 2. BrowserRouter (navigation)
// 3. AuthProvider (needs Router for useNavigate)
// 4. NotificationProvider (needs AuthProvider for isAuthenticated)
//
// Routes:
// /login — public
// / + /dashboard — private, dashboard
// /data-entry — private, data entry
// /metric-history — private, metric history
// /reports — private, reports
// /admin/* — private + requireAdmin
// /accessibility-statement — private, accessibility

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AppLayout } from './components/layout';
import PrivateRoute from './components/PrivateRoute';

// Pages — v0.1.0
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AccessibilityStatement from './pages/AccessibilityStatement';

// Pages — v0.2.0 (admin)
import AdminUnits from './pages/admin/AdminUnits';
import AdminClassifications from './pages/admin/AdminClassifications';
import AdminUsers from './pages/admin/AdminUsers';
import CompanySettings from './pages/admin/CompanySettings';

// Pages — v0.3.0+ (placeholders)
import PlaceholderPage from './pages/PlaceholderPage';

// Test harness — initialized on load if REACT_APP_TEST_MODE=true
import { setupTestHarness } from './services/testHarness';
setupTestHarness();

export default function App() {
  return (
    <ThemeContextProvider>
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />

              {/* Private routes — wrapped in AppLayout */}
              <Route element={<PrivateRoute />}>
                <Route element={<AppLayout />}>
                  {/* Dashboard */}
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />

                  {/* Data entry — v0.4.0 */}
                  <Route path="/data-entry" element={<PlaceholderPage pageKey="dataEntry" />} />

                  {/* Metric history — v0.4.0 */}
                  <Route path="/metric-history" element={<PlaceholderPage pageKey="metricHistory" />} />

                  {/* Reports — v0.4.0 */}
                  <Route path="/reports" element={<PlaceholderPage pageKey="reports" />} />

                  {/* Accessibility statement */}
                  <Route path="/accessibility-statement" element={<AccessibilityStatement />} />

                  {/* Admin routes — requireAdmin */}
                  <Route element={<PrivateRoute requireAdmin />}>
                    {/* v0.2.0 — real pages */}
                    <Route path="/admin/units" element={<AdminUnits />} />
                    <Route path="/admin/classifications" element={<AdminClassifications />} />
                    <Route path="/admin/users" element={<AdminUsers />} />
                    <Route path="/admin/settings" element={<CompanySettings />} />

                    {/* v0.3.0 — placeholders */}
                    <Route path="/admin/categories" element={<PlaceholderPage pageKey="categories" />} />
                    <Route path="/admin/metric-types" element={<PlaceholderPage pageKey="metricTypes" />} />
                    <Route path="/admin/metrics" element={<PlaceholderPage pageKey="metrics" />} />
                  </Route>

                  {/* 404 — redirect to dashboard */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Route>
              </Route>
            </Routes>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeContextProvider>
  );
}
